import Api from "./ciApi";
import { combineNationalDataForBarCharts, calculateTopEightBarCharts, combineFilters } from "./modulesCommon";
import { combineAndCommit, clone } from "../helpers";

// TYPES
// patient data tab
const PATIENT_COMORBIDITIES = "PATIENT_COMORBIDITIES";

// general data tab
const FUNDING = "FUNDING";
const IMAGE_FINDING_MAIN_QUESTION = "IMAGE_FINDING_MAIN_QUESTION";
const IMAGE_FINDING_SUB_QUESTION = "IMAGE_FINDING_SUB_QUESTION";
const ABNORMAL_FINDINGS = "ABNORMAL_FINDINGS";
const COCHLEAR_MALFORMATION = "COCHLEAR_MALFORMATION";
const COCHLEAR_NERVE_DISORDER = "COCHLEAR_NERVE_DISORDER";
const AETIOLOGY = "AETIOLOGY";
const COMMUNICATION_MODE = "COMMUNICATION_MODE";
const ONSET = "ONSET";
const TYPE = "TYPE";
const AETIOLOGY_GENETIC = "AETIOLOGY_GENETIC";
const AETIOLOGY_INFECTIVE = "AETIOLOGY_INFECTIVE";
const AETIOLOGY_NEONATAL_PREMATURITY = "AETIOLOGY_NEONATAL_PREMATURITY";
const AETIOLOGY_SYNDROMAL = "AETIOLOGY_SYNDROMAL";

// surgery data tab
const SURGERY_TYPE = "SURGERY_TYPE";
const MANUFACTURER = "MANUFACTURER";
const REASON = "REASON";

// surgery data tab implants by manufacturer
const IMPLANTS_BY_MANUFACTURER = "IMPLANTS_BY_MANUFACTURER";
const COCHLEAR_IMPLANT = "COCHLEAR_IMPLANT";
const ADVANCED_BIONICS_IMPLANT = "ADVANCED_BIONICS_IMPLANT";
const ADVANCED_BIONICS_ELECTRODE = "ADVANCED_BIONICS_ELECTRODE";
const MED_EL_IMPLANT = "MED_EL_IMPLANT";
const MED_EL_ELECTRODE = "MED_EL_ELECTRODE";
const MED_EL_PIN = "MED_EL_PIN";
const OTICON_MEDICAL_IMPLANT = "OTICON_MEDICAL_IMPLANT";

// surgery data tab intra op data
const NRT_RESPONSE = "NRT_RESPONSE";
const COMPLICATIONS = "COMPLICATIONS";
const COMPLICATION_DETAILS = "COMPLICATION_DETAILS";
const ELECTRODE_INSERTION = "ELECTRODE_INSERTION";
const ELECTRODE_RELATED_ISSUES = "ELECTRODE_RELATED_ISSUES";

// surgery data tab revisions data
const REASON_FOR_REVISION = "REASON_FOR_REVISION";
const REVISION_COMPLICATIONS_MAIN_QUESTION = "REVISION_COMPLICATIONS_MAIN_QUESTION";
const REVISION_COMPLICATIONS_SUB_QUESTION = "REVISION_COMPLICATIONS_SUB_QUESTION";

// follow up data tab
const HOSPITAL_STAY = "HOSPITAL_STAY";
const FOLLOW_UP_COMPLICATIONS_MAIN_QUESTION = "FOLLOW_UP_COMPLICATIONS_MAIN_QUESTION";
const FOLLOW_UP_COMPLICATIONS_SUB_QUESTION = "FOLLOW_UP_COMPLICATIONS_SUB_QUESTION";

// ci status data tab
// manufacturer
const SOUND_PROCESSOR_MANUFACTURER = "SOUND_PROCESSOR_MANUFACTURER";
const MANUFACTURER_COCHLEAR = "MANUFACTURER_COCHLEAR";
const MANUFACTURER_ADVANCED_BIONICS = "MANUFACTURER_ADVANCED_BIONICS";
const MANUFACTURER_MED_EL = "MANUFACTURER_MED_EL";
const MANUFACTURER_OTICON_MEDICAL = "MANUFACTURER_OTICON_MEDICAL";

// implant status
const IMPLANT_STATUS = "IMPLANT_STATUS";
const STATUS_CHARACTERISTICS_DECREMENTS_PERFORMANCE = "STATUS_CHARACTERISTICS_DECREMENTS_PERFORMANCE";
const STATUS_ELECTRODE_RELATED_ISSUES = "STATUS_ELECTRODE_RELATED_ISSUES";
const STATUS_MEDICAL_STATUS_MAIN_QUESTION = "STATUS_MEDICAL_STATUS_MAIN_QUESTION";
const STATUS_MEDICAL_STATUS_SUB_QUESTION = "STATUS_MEDICAL_STATUS_SUB_QUESTION";

// performance tests
const PREOP_SSQ_TYPES = "PREOP_SSQ_TYPES";
const POSTOP_SSQ_TYPES = "POSTOP_SSQ_TYPES";

// other
const DATA = "DATA";
const DATA_LOADED = "DATA_LOADED";
const DATA_LOADING = "DATA_LOADING";
const NATIONAL_DATA = "NATIONAL_DATA";
const NATIONAL_DATA_LOADED = "NATIONAL_DATA_LOADED";
const MR_ATTRIBUTES = "MR_ATTRIBUTES";

const emptyDataObj = {
    patientDemographics: {},
    patientComorbidities: {},
    general: {
        funding: {},
        imaging: {
            imagingFinding: { mainQuestion: {}, subQuestion: {} },
            abnormalFindings: {},
            cochlearMalformation: {},
            cochlearNerveDisorder: {},
        },
        hearingLoss: {
            aetiology: {},
            communicationMode: {},
            onset: {},
            type: {},
            aetiologyGenetic: {},
            aetiologyInfective: {},
            aetiologyNeonatalPrematurity: {},
            aetiologySyndromal: {},
        },
    },
    surgery: {
        surgeryStatistics: { surgeryType: {} },
        explantations: { manufacturer: {}, reason: {} },
        implants: {
            implantsByManufacturer: {},
            cochlearImplant: {},
            advancedBionicsImplant: {},
            advancedBionicsElectrode: {},
            medElImplant: {},
            medElElectrode: {},
            medElPin: {},
            oticonMedicalImplant: {},
        },
        intraOpData: {
            complicationDetails: {},
            complications: {},
            electrodeInsertion: {},
            electrodeRelatedIssues: {},
            nrtResponse: {},
        },
        revisions: { reasonForRevision: {}, complications: { mainQuestion: {}, subQuestion: {} } },
    },
    followUp: { hospitalStay: {}, complications: { mainQuestion: {}, subQuestion: {} } },
    ciStatus: {
        soundProcessor: { manufacturer: {}, cochlear: {}, advancedBionics: {}, medEl: {}, oticonMedical: {} },
        implantStatus: {
            implantStatus: {},
            characteristicDecrementsPerformance: {},
            electrodeRelatedIssues: {},
            medicalStatus: { mainQuestion: {}, subQuestion: {} },
        },
    },
    performanceTests: {
        postOpTests: { speechSpatialQualities: { typeOfSsq: {} } },
        preOpTests: { speechSpatialQualities: { typeOfSsq: {} } },
    },
};

const fields = [
    // patient data tab
    {
        commitTo: PATIENT_COMORBIDITIES,
        path: ["patientComorbidities"],
        calculateTopEight: true,
    },
    // general data tab
    {
        commitTo: FUNDING,
        path: ["general", "funding"],
    },
    {
        commitTo: IMAGE_FINDING_MAIN_QUESTION,
        path: ["general", "imaging", "imagingFinding", "mainQuestion"],
    },
    {
        commitTo: IMAGE_FINDING_SUB_QUESTION,
        path: ["general", "imaging", "imagingFinding", "subQuestion"],
    },
    {
        commitTo: ABNORMAL_FINDINGS,
        path: ["general", "imaging", "abnormalFindings"],
    },
    {
        commitTo: COCHLEAR_MALFORMATION,
        path: ["general", "imaging", "cochlearMalformation"],
    },
    {
        commitTo: COCHLEAR_NERVE_DISORDER,
        path: ["general", "imaging", "cochlearNerveDisorder"],
    },
    {
        commitTo: AETIOLOGY,
        path: ["general", "hearingLoss", "aetiology"],
    },
    {
        commitTo: COMMUNICATION_MODE,
        path: ["general", "hearingLoss", "communicationMode"],
    },
    {
        commitTo: ONSET,
        path: ["general", "hearingLoss", "onset"],
    },
    {
        commitTo: TYPE,
        path: ["general", "hearingLoss", "type"],
    },
    {
        commitTo: AETIOLOGY_GENETIC,
        path: ["general", "hearingLoss", "aetiologyGenetic"],
    },
    {
        commitTo: AETIOLOGY_INFECTIVE,
        path: ["general", "hearingLoss", "aetiologyInfective"],
    },
    {
        commitTo: AETIOLOGY_NEONATAL_PREMATURITY,
        path: ["general", "hearingLoss", "aetiologyNeonatalPrematurity"],
    },
    {
        commitTo: AETIOLOGY_SYNDROMAL,
        path: ["general", "hearingLoss", "aetiologySyndromal"],
    },
    // surgery data tab
    {
        commitTo: SURGERY_TYPE,
        path: ["surgery", "surgeryStatistics", "surgeryType"],
    },
    {
        commitTo: MANUFACTURER,
        path: ["surgery", "explantations", "manufacturer"],
    },
    {
        commitTo: REASON,
        path: ["surgery", "explantations", "reason"],
    },
    // surgery data tab implants by manufacturer
    {
        commitTo: IMPLANTS_BY_MANUFACTURER,
        path: ["surgery", "implants", "implantsByManufacturer"],
    },
    {
        commitTo: COCHLEAR_IMPLANT,
        path: ["surgery", "implants", "cochlearImplant"],
    },
    {
        commitTo: ADVANCED_BIONICS_IMPLANT,
        path: ["surgery", "implants", "advancedBionicsImplant"],
    },
    {
        commitTo: ADVANCED_BIONICS_ELECTRODE,
        path: ["surgery", "implants", "advancedBionicsElectrode"],
    },
    {
        commitTo: MED_EL_IMPLANT,
        path: ["surgery", "implants", "medElImplant"],
    },
    {
        commitTo: MED_EL_ELECTRODE,
        path: ["surgery", "implants", "medElElectrode"],
    },
    {
        commitTo: MED_EL_PIN,
        path: ["surgery", "implants", "medElPin"],
    },
    {
        commitTo: OTICON_MEDICAL_IMPLANT,
        path: ["surgery", "implants", "oticonMedicalImplant"],
    },
    // surgery data tab intra op data
    {
        commitTo: NRT_RESPONSE,
        path: ["surgery", "intraOpData", "nrtResponse"],
    },
    {
        commitTo: COMPLICATIONS,
        path: ["surgery", "intraOpData", "complications"],
    },
    {
        commitTo: COMPLICATION_DETAILS,
        path: ["surgery", "intraOpData", "complicationDetails"],
    },
    {
        commitTo: ELECTRODE_INSERTION,
        path: ["surgery", "intraOpData", "electrodeInsertion"],
    },
    {
        commitTo: ELECTRODE_RELATED_ISSUES,
        path: ["surgery", "intraOpData", "electrodeRelatedIssues"],
    },
    // surgery data tab revisions
    {
        commitTo: REASON_FOR_REVISION,
        path: ["surgery", "revisions", "reasonForRevision"],
    },
    {
        commitTo: REVISION_COMPLICATIONS_MAIN_QUESTION,
        path: ["surgery", "revisions", "complications", "mainQuestion"],
    },
    {
        commitTo: REVISION_COMPLICATIONS_SUB_QUESTION,
        path: ["surgery", "revisions", "complications", "subQuestion"],
    },
    // follow up data tab
    {
        commitTo: HOSPITAL_STAY,
        path: ["followUp", "hospitalStay"],
    },
    {
        commitTo: FOLLOW_UP_COMPLICATIONS_MAIN_QUESTION,
        path: ["followUp", "complications", "mainQuestion"],
    },
    {
        commitTo: FOLLOW_UP_COMPLICATIONS_SUB_QUESTION,
        path: ["followUp", "complications", "subQuestion"],
    },
    // ci status data tab
    // manufacturer
    {
        commitTo: SOUND_PROCESSOR_MANUFACTURER,
        path: ["ciStatus", "soundProcessor", "manufacturer"],
    },
    {
        commitTo: MANUFACTURER_COCHLEAR,
        path: ["ciStatus", "soundProcessor", "cochlear"],
    },
    {
        commitTo: MANUFACTURER_ADVANCED_BIONICS,
        path: ["ciStatus", "soundProcessor", "advancedBionics"],
    },
    {
        commitTo: MANUFACTURER_MED_EL,
        path: ["ciStatus", "soundProcessor", "medEl"],
    },
    {
        commitTo: MANUFACTURER_OTICON_MEDICAL,
        path: ["ciStatus", "soundProcessor", "oticonMedical"],
    },
    // implant status
    {
        commitTo: IMPLANT_STATUS,
        path: ["ciStatus", "implantStatus", "implantStatus"],
    },
    {
        commitTo: STATUS_CHARACTERISTICS_DECREMENTS_PERFORMANCE,
        path: ["ciStatus", "implantStatus", "characteristicDecrementsPerformance"],
    },
    {
        commitTo: STATUS_ELECTRODE_RELATED_ISSUES,
        path: ["ciStatus", "implantStatus", "electrodeRelatedIssues"],
    },
    {
        commitTo: STATUS_MEDICAL_STATUS_MAIN_QUESTION,
        path: ["ciStatus", "implantStatus", "medicalStatus", "mainQuestion"],
    },
    {
        commitTo: STATUS_MEDICAL_STATUS_SUB_QUESTION,
        path: ["ciStatus", "implantStatus", "medicalStatus", "subQuestion"],
    },
    // performance tests
    {
        commitTo: PREOP_SSQ_TYPES,
        path: ["performanceTests", "preOpTests", "speechSpatialQualities", "typeOfSsq"],
    },
    {
        commitTo: POSTOP_SSQ_TYPES,
        path: ["performanceTests", "postOpTests", "speechSpatialQualities", "typeOfSsq"],
    },
];

const ciDataModule = {
    namespaced: true,
    state: () => ({
        data: clone(emptyDataObj),
        dataLoaded: null,
        dataLoading: false,
        nationalData: clone(emptyDataObj),
        nationalDataLoaded: false,
        mrAttributes: Object.create({}),
    }),
    mutations: {
        // patient data tab
        [PATIENT_COMORBIDITIES](state, obj) {
            state.data.patientComorbidities = obj;
        },

        // general data tab
        [FUNDING](state, obj) {
            state.data.general.funding = obj;
        },
        [IMAGE_FINDING_MAIN_QUESTION](state, obj) {
            state.data.imaging.imagingFinding.mainQuestion = obj;
        },
        [IMAGE_FINDING_SUB_QUESTION](state, obj) {
            state.data.imaging.imagingFinding.subQuestion = obj;
        },
        [ABNORMAL_FINDINGS](state, obj) {
            state.data.imaging.abnormalFindings = obj;
        },
        [COCHLEAR_MALFORMATION](state, obj) {
            state.data.imaging.cochlearMalformation = obj;
        },
        [COCHLEAR_NERVE_DISORDER](state, obj) {
            state.data.imaging.cochlearNerveDisorder = obj;
        },
        [AETIOLOGY](state, obj) {
            state.data.hearingLoss.aetiology = obj;
        },
        [COMMUNICATION_MODE](state, obj) {
            state.data.hearingLoss.communicationMode = obj;
        },
        [ONSET](state, obj) {
            state.data.hearingLoss.onset = obj;
        },
        [TYPE](state, obj) {
            state.data.hearingLoss.type = obj;
        },
        [AETIOLOGY_GENETIC](state, obj) {
            state.data.hearingLoss.aetiologyGenetic = obj;
        },
        [AETIOLOGY_INFECTIVE](state, obj) {
            state.data.hearingLoss.aetiologyInfective = obj;
        },
        [AETIOLOGY_NEONATAL_PREMATURITY](state, obj) {
            state.data.hearingLoss.aetiologyNeonatalPrematurity = obj;
        },
        [AETIOLOGY_SYNDROMAL](state, obj) {
            state.data.hearingLoss.aetiologySyndromal = obj;
        },

        // surgery data tab
        [SURGERY_TYPE](state, obj) {
            state.data.surgery.surgeryStatistics.surgeryType = obj;
        },
        [MANUFACTURER](state, obj) {
            state.data.surgery.explantations.manufacturer = obj;
        },
        [REASON](state, obj) {
            state.data.surgery.explantations.reson = obj;
        },

        // surgery data tab implants by manufacturer
        [IMPLANTS_BY_MANUFACTURER](state, obj) {
            state.data.surgery.implants.implantsByManufacturer = obj;
        },
        [COCHLEAR_IMPLANT](state, obj) {
            state.data.surgery.implants.cochlearImplant = obj;
        },
        [ADVANCED_BIONICS_IMPLANT](state, obj) {
            state.data.surgery.implants.advancedBionicsImplant = obj;
        },
        [ADVANCED_BIONICS_ELECTRODE](state, obj) {
            state.data.surgery.implants.advancedBionicsElectrode = obj;
        },
        [MED_EL_IMPLANT](state, obj) {
            state.data.surgery.implants.medElImplant = obj;
        },
        [MED_EL_ELECTRODE](state, obj) {
            state.data.surgery.implants.medElElectrode = obj;
        },
        [MED_EL_PIN](state, obj) {
            state.data.surgery.implants.medElPin = obj;
        },
        [OTICON_MEDICAL_IMPLANT](state, obj) {
            state.data.surgery.implants.oticonMedicalImplant = obj;
        },

        // surgery data tab intra op data
        [NRT_RESPONSE](state, obj) {
            state.data.surgery.intraOpData.nrtResponse = obj;
        },
        [COMPLICATIONS](state, obj) {
            state.data.surgery.intraOpData.complications = obj;
        },
        [COMPLICATION_DETAILS](state, obj) {
            state.data.surgery.intraOpData.complicationDetails = obj;
        },
        [ELECTRODE_INSERTION](state, obj) {
            state.data.surgery.intraOpData.electrodeInsertion = obj;
        },
        [ELECTRODE_RELATED_ISSUES](state, obj) {
            state.data.surgery.intraOpData.electrodeRelatedIssues = obj;
        },

        // surgery data tab revisions data
        [REASON_FOR_REVISION](state, obj) {
            state.data.surgery.revisions.reasonForRevision = obj;
        },
        [REVISION_COMPLICATIONS_MAIN_QUESTION](state, obj) {
            state.data.surgery.revisions.complications.mainQuestion = obj;
        },
        [REVISION_COMPLICATIONS_SUB_QUESTION](state, obj) {
            state.data.surgery.revisions.complications.subQuestion = obj;
        },

        // follow up data tab
        [HOSPITAL_STAY](state, obj) {
            state.data.followUp.hospitalStay = obj;
        },
        [FOLLOW_UP_COMPLICATIONS_MAIN_QUESTION](state, obj) {
            state.data.followUp.complications.mainQuestion = obj;
        },
        [FOLLOW_UP_COMPLICATIONS_SUB_QUESTION](state, obj) {
            state.data.followUp.complications.subQuestion = obj;
        },

        // ci status data tab
        // manufacturer
        [SOUND_PROCESSOR_MANUFACTURER](state, obj) {
            state.data.ciStatus.soundProcessor.manufacturer = obj;
        },
        [MANUFACTURER_COCHLEAR](state, obj) {
            state.data.ciStatus.soundProcessor.cochlear = obj;
        },
        [MANUFACTURER_ADVANCED_BIONICS](state, obj) {
            state.data.ciStatus.soundProcessor.advancedBionics = obj;
        },
        [MANUFACTURER_MED_EL](state, obj) {
            state.data.ciStatus.soundProcessor.medEl = obj;
        },
        [MANUFACTURER_OTICON_MEDICAL](state, obj) {
            state.data.ciStatus.soundProcessor.oticonMedical = obj;
        },
        // implant status
        [IMPLANT_STATUS](state, obj) {
            state.data.ciStatus.implantStatus.implantStatus = obj;
        },
        [STATUS_CHARACTERISTICS_DECREMENTS_PERFORMANCE](state, obj) {
            state.data.ciStatus.implantStatus.characteristicDecrementsPerformance = obj;
        },
        [STATUS_ELECTRODE_RELATED_ISSUES](state, obj) {
            state.data.ciStatus.implantStatus.electrodeRelatedIssues = obj;
        },
        [STATUS_MEDICAL_STATUS_MAIN_QUESTION](state, obj) {
            state.data.ciStatus.implantStatus.medicalStatus.mainQuestion = obj;
        },
        [STATUS_MEDICAL_STATUS_SUB_QUESTION](state, obj) {
            state.data.ciStatus.implantStatus.medicalStatus.subQuestion = obj;
        },
        // performance tests
        [PREOP_SSQ_TYPES](state, obj) {
            state.data.performanceTests.preOpTests.speechSpatialQualities.typeOfSsq = obj;
        },
        [POSTOP_SSQ_TYPES](state, obj) {
            state.data.performanceTests.postOpTests.speechSpatialQualities.typeOfSsq = obj;
        },

        // other
        [DATA](state, data) {
            state.data = data;
        },
        [DATA_LOADED](state, isDataLoaded) {
            state.dataLoaded = isDataLoaded;
        },
        [DATA_LOADING](state, dataLoading) {
            state.dataLoading = dataLoading;
        },
        [NATIONAL_DATA](state, obj) {
            state.nationalData = obj;
        },
        [NATIONAL_DATA_LOADED](state, isNationalDataLoaded) {
            state.nationalDataLoaded = isNationalDataLoaded;
        },
        [MR_ATTRIBUTES](state, mrAttributes) {
            state.mrAttributes = mrAttributes;
        },
    },
    actions: {
        readData({ commit, state, rootState }, context) {
            let filters = combineFilters(rootState.patientQuery, rootState.caseQuery, rootState.advancedQueryCriteria);
            commit(DATA_LOADED, false);

            return Api.GetData(context, filters).then(function (result) {
                calculateTopEightBarCharts(fields, result.data);

                // if context data should be combined with national data
                if (rootState.dataModule.system.isNationalDataNeeded)
                    combineNationalDataForBarCharts(fields, result.data, state.nationalData);

                commit(DATA, result.data);
                commit(DATA_LOADED, true);
            });
        },
        readNationalData({ commit, state, rootState }) {
            commit(NATIONAL_DATA_LOADED, false);
            let filters = combineFilters(rootState.patientQuery, rootState.caseQuery, rootState.advancedQueryCriteria);
            return Api.GetNationalData(filters, rootState).then(function (result) {
                fields.forEach((element) => {
                    let statePath = state.data;
                    let resultPath = result.data;

                    for (let index = 0; index < element.path.length; index++) {
                        statePath = statePath[element.path[index]];
                        resultPath = resultPath[element.path[index]];
                    }

                    combineAndCommit(commit, element.commitTo, statePath, resultPath);
                });

                commit(NATIONAL_DATA, result.data);
                commit(NATIONAL_DATA_LOADED, true);
            });
        },
        // is data was loaded
        setDataLoaded({ commit }, isDataLoaded) {
            return commit(DATA_LOADED, isDataLoaded);
        },
        setDataLoading({ commit }, isDataLoading) {
            return commit(DATA_LOADING, isDataLoading);
        },
        cleanData({ commit }) {
            commit(DATA, clone(emptyDataObj));
            commit(DATA_LOADED, false);
        },
        readMrAttributes({ commit }) {
            return Api.GetMrAttributes().then((result) => {
                commit(MR_ATTRIBUTES, result.data);
            });
        },
    },
};

export default ciDataModule;
